/**
 * A journey's access badges and who funds it — ONE component for every surface
 * that shows a journey (the header switcher, "My journeys", Billing).
 *
 * The two surfaces used to draw their own: the switcher showed Unlocked, Full
 * Access and Sponsored, and the "My journeys" card showed Full Access alone for
 * the very same journey. Rendering both from here means a citizen reads the
 * same facts about a journey wherever they meet it, and a new badge cannot be
 * added to one surface and forgotten on the other.
 *
 * ─── What is and is not derived here ─────────────────────────────────────
 *
 * The ACCESS badges — unlocked, full/basic, sponsored, family, Pro, invited,
 * and whether the company name is an invitation attribution — are the
 * server's `tierBadge`, computed once by `journeyTierBadge` in
 * @tonnie/foundation. Absent from an older server: then NO access badge
 * renders, rather than one guessed here. What remains is display state read
 * off `lifecycleState` and the dates.
 *
 * Each tag's colour is `JOURNEY_ACCESS_TONE` (@tonnie/design-tokens), the map
 * the phone's switcher reads too — so one journey wears the same tags in both.
 *
 * `data-tier` / `data-funding` are the machine-readable half: a test must never
 * assert on a CSS class, because `@tonnie/ui` variants are unconditional.
 */
import { Lock, Unlock } from '@tonnie/assets';
import { JOURNEY_ACCESS_TONE, type JourneyAccessTag } from '@tonnie/design-tokens';
import { useTranslation } from '@tonnie/locale';
import { Badge, TONE_CLASSES, cn } from '@tonnie/ui';

import { useAuth } from '@/core/contexts/AuthContext';
import type { JourneyInstance } from '@/core/contexts/AuthContext';

/**
 * What the badges read off a journey. Narrower than `JourneyInstance` so every
 * surface's journey row — the auth context's and Billing's — renders through
 * this one component rather than keeping its own copy of the badges.
 */
export interface IJourneyAccessFacts {
  tierBadge?: JourneyInstance['tierBadge'];
  lifecycleState: string;
  sponsorName?: string;
  paymentInfo?: { expirationDate?: string };
  expiresAt?: string | Date | null;
}

/** A journey is expired (lifecycle, or a paid plan ran out). */
export function isJourneyExpired(instance: IJourneyAccessFacts): boolean {
  if (instance.lifecycleState === 'expired') return true;
  if (instance.paymentInfo?.expirationDate) {
    return new Date(instance.paymentInfo.expirationDate) < new Date();
  }
  if (instance.expiresAt) {
    return new Date(instance.expiresAt) < new Date();
  }
  return false;
}

/** When access ends — the payment's own date first, the journey's otherwise. */
export function journeyExpirationDate(instance: JourneyInstance): string | undefined {
  return instance.paymentInfo?.expirationDate || instance.expiresAt;
}

/** A tag's tint, text and line, in its shared tone. */
function toneOf(tag: JourneyAccessTag): string {
  const tone = TONE_CLASSES[JOURNEY_ACCESS_TONE[tag]];
  return cn(tone.subtle, tone.text, tone.line);
}

interface IJourneyAccessBadgesProps {
  instance: IJourneyAccessFacts;
  className?: string;
}

export function JourneyAccessBadges({ instance, className }: IJourneyAccessBadgesProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const badge = instance.tierBadge;
  const unlocked = badge?.tier === 'full';
  const expired = isJourneyExpired(instance);
  const paused = instance.lifecycleState === 'paused';
  const pendingSetup = instance.lifecycleState === 'pending_setup';
  const isGovSponsored = badge?.funding === 'sponsored_government';
  const isSponsored = badge?.funding === 'sponsored_company' || isGovSponsored;
  const isFamily = badge?.funding === 'family';
  const isInvitedOnly = badge?.funding === 'invited';
  const isPro = badge?.funding === 'pro';
  const isFamilyMemberViewing = user?.role === 'family_member' && isFamily;

  // The family is named by its account holder's surname: the viewer's own for
  // the master, the sponsor's (the resolved inviter) for everyone else.
  const familyLabel = (() => {
    if (isFamily && user?.lastName) return user.lastName;
    if (instance.sponsorName) {
      const parts = instance.sponsorName.trim().split(/\s+/);
      return parts[parts.length - 1];
    }
    return '';
  })();

  return (
    <div className={cn('space-y-2', className)} data-testid="journey-access-badges">
      {isFamilyMemberViewing && (
        <Badge className="bg-warning/10 text-warning-accessible border-warning/20 text-xs">
          {t('payment.citizenOnly')}
        </Badge>
      )}

      <div className="flex items-center gap-1.5 flex-wrap">
        {badge && (
          <>
            {unlocked ? (
              <Badge variant="outline" className={cn('text-xs', toneOf('unlocked'))}>
                <Unlock className="w-3 h-3 mr-1" />
                {t('journey.selector.unlocked')}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">
                <Lock className="w-3 h-3 mr-1" />
                {t('journey.selector.locked')}
              </Badge>
            )}
            <Badge
              variant="outline"
              data-testid="journey-tier-badge"
              data-tier={badge.tier}
              data-funding={badge.funding}
              className={cn(
                'text-xs',
                unlocked ? toneOf('fullAccess') : 'bg-muted text-muted-foreground'
              )}
            >
              {unlocked ? t('journey.selector.fullAccess') : t('journey.selector.basicAccess')}
            </Badge>
          </>
        )}

        {/* Accepted an invitation, onboarding not finished yet. */}
        {pendingSetup && (
          <Badge variant="outline" className={cn('text-xs font-medium', toneOf('setupPending'))}>
            {t('journey.selector.setupPending')}
          </Badge>
        )}

        {/* Sponsorship payment failure or an admin pause. */}
        {paused && (
          <Badge variant="outline" className={cn('text-xs font-medium', toneOf('paused'))}>
            {t('journey.selector.paused')}
          </Badge>
        )}

        {isSponsored && (
          <Badge
            variant="outline"
            className={cn(
              'text-xs font-medium',
              toneOf(isGovSponsored ? 'sponsoredGovernment' : 'sponsoredCompany')
            )}
          >
            <span className="mr-1" aria-hidden="true">
              {isGovSponsored ? '🟣' : '🔵'}
            </span>
            {t('journey.selector.sponsored')}
          </Badge>
        )}

        {isFamily && (
          <Badge variant="outline" className={cn('text-xs font-medium', toneOf('family'))}>
            <span className="mr-1" aria-hidden="true">
              🟡
            </span>
            {t('journey.selector.family')}
          </Badge>
        )}

        {isPro && (
          <Badge variant="outline" className={cn('text-xs font-medium', toneOf('pro'))}>
            <span className="mr-1" aria-hidden="true">
              🟣
            </span>
            {t('common.plan.pro')}
          </Badge>
        )}

        {/* Invited-only — explore mode. */}
        {isInvitedOnly && !pendingSetup && (
          <Badge variant="outline" className={cn('text-xs font-medium', toneOf('invited'))}>
            {t('journey.selector.invitedOnly')}
          </Badge>
        )}

        {expired && (
          <Badge variant="outline" className={cn('text-xs font-medium', toneOf('expired'))}>
            {t('journey.selector.expired')}
          </Badge>
        )}
      </div>

      {/* Who funds it. */}
      {isSponsored && instance.sponsorName && (
        <p className="text-xs text-muted-foreground">
          {isGovSponsored ? t('journey.selector.government') : t('journey.selector.company')}:{' '}
          {instance.sponsorName}
        </p>
      )}
      {isFamily && familyLabel && (
        <p className="text-xs text-muted-foreground">
          {t('journey.selector.family')} {familyLabel}
        </p>
      )}
    </div>
  );
}
